import { useEffect, useState } from "react";
import axios from "axios";
import { LinkBreak, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import { API } from "@/lib/api";

const field = "w-full bg-[#111] border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-crimson";
const btn = "flex items-center gap-1.5 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 disabled:opacity-40";

const BrokenRow = ({ item, headers, onFixed }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const fix = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/broken-links/${item.video_id}`, { source_url: url }, { headers, withCredentials: true });
      toast.success(`${item.title_override || item.title} is back online`);
      onFixed(item.video_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not restore");
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="grid gap-2 border-b border-white/5 py-3 md:grid-cols-[1fr_1.2fr_auto] md:items-center" data-testid={`broken-row-${item.video_id}`}>
      <div className="min-w-0">
        <p className="truncate text-sm text-white/80">{item.title_override || item.title}</p>
        <p className="font-mono text-[10px] text-white/35 truncate">{item.source_channel || "—"} · {item.broken_at ? new Date(item.broken_at).toLocaleDateString() : ""} · {item.monetized_embed_url}</p>
      </div>
      <input data-testid={`broken-source-input-${item.video_id}`} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="New YouTube URL or video ID" className={field} />
      <button data-testid={`broken-restore-${item.video_id}`} onClick={fix} disabled={busy || !url.trim()} className={`${btn} border-emerald-400/50 text-emerald-300 hover:bg-emerald-400 hover:text-black`}>
        Restore
      </button>
    </li>
  );
};

export const BrokenLinksPanel = ({ headers }) => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/broken-links`, { headers, withCredentials: true });
      setItems(data.items);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load broken links");
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const sweep = async () => {
    await axios.post(`${API}/admin/broken-links/sweep`, {}, { headers, withCredentials: true });
    toast.success("Dead-link sweep started — refresh this list in a few minutes");
  };
  return (
    <section data-testid="broken-links-panel" className="border border-crimson/40 bg-crimson/[0.04] p-6 sm:p-8">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <LinkBreak size={22} weight="duotone" className="text-crimson" />
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">Broken Links</h2>
        <span data-testid="broken-links-count" className="rounded-full bg-crimson px-2 py-0.5 font-mono text-[10px] text-white">{items.length}</span>
        <div className="ml-auto flex gap-2">
          <button data-testid="broken-links-refresh" onClick={load} className={`${btn} border-white/20 text-white/70 hover:border-amber hover:text-amber`}><ArrowsClockwise size={13} /> Refresh</button>
          <button data-testid="broken-links-sweep" onClick={sweep} className={`${btn} border-crimson/60 text-crimson hover:bg-crimson hover:text-white`}>Run Sweep Now</button>
        </div>
      </div>
      <p className="mb-2 font-mono text-[11px] text-white/35">Titles removed by the uploader are hidden from the catalog automatically (checked daily and on view). Paste a new source to bring one back.</p>
      {loaded && items.length === 0 && <p data-testid="broken-links-empty" className="py-4 font-mono text-[12px] text-emerald-300/80">All sources are live.</p>}
      <ul>{items.map((it) => <BrokenRow key={it.video_id} item={it} headers={headers} onFixed={(id) => setItems((l) => l.filter((x) => x.video_id !== id))} />)}</ul>
    </section>
  );
};
